import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Camera, StickyNote, MapPin, ArrowLeft, FileText, ShieldCheck, RefreshCw, Check, X } from "lucide-react";
import { useIsFetching } from "@tanstack/react-query";
import { CloudOff } from "lucide-react";
import { Button, Pill, type PillProps, SeverityChip, HeimdallAttribution, EmptyState, Textarea } from "@/design/components";
import { formatRelativeShort } from "@/design/format";
import { cn } from "@/design/cn";
import { useProjects, useMilestones, useReports, useSiteVisits, usePeopleIndex, useAnalystName, useSiteVisitQuestions } from "@/domain/hooks";
import { useOnlineStatus } from "@/domain/useOnlineStatus";
import { useOfflineQueue } from "@/domain/offlineQueue";
import { useFileDrop } from "@/domain/useFileDrop";
import {
  useBeginVisit,
  useChecklistActions,
  useCompleteVisit,
  useGenerateVisitReport,
  useApproveVisitReport,
  useSyncVisitQuestions,
} from "@/domain/useMutations";
import type { VisitJudgement, VisitChecklistItem, VisitCapture } from "@/domain/types";
import { toast } from "sonner";

// One source of truth for the checklist judgement vocabulary: label + semantic colour.
// as-claimed = success, partial = warn, not-as-claimed = danger, unverified = neutral.
const JUDGEMENT_META: Record<VisitJudgement, { label: string; variant: NonNullable<PillProps["variant"]> }> = {
  as_claimed: { label: "As claimed", variant: "success" },
  partial: { label: "Partial", variant: "warn" },
  not_as_claimed: { label: "Not as claimed", variant: "danger" },
  unverified: { label: "Could not verify", variant: "neutral" },
};

const JUDGEMENT_ORDER: VisitJudgement[] = ["as_claimed", "partial", "not_as_claimed", "unverified"];

export function SiteVisitView() {
  const { visitId } = useParams();
  const visits = useSiteVisits();
  const isFetchingVisits = useIsFetching({ queryKey: ["site-visits"] }) > 0;
  const projects = useProjects();
  const people = usePeopleIndex();
  const analystName = useAnalystName();
  const { begin, isPending: beginning } = useBeginVisit();
  const { judge, capture } = useChecklistActions(visitId ?? "");
  const { sync } = useSyncVisitQuestions();
  const questions = useSiteVisitQuestions();
  const { complete, isPending: completing } = useCompleteVisit();
  const { generate, isPending: generating } = useGenerateVisitReport();
  const { approve: approveVisit, isPending: approvingVisit } = useApproveVisitReport();
  const [summary, setSummary] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const isOnline = useOnlineStatus();
  const { pending, syncing: queueSyncing } = useOfflineQueue();

  const visit = visits.find((v) => v.id === visitId);

  // These hooks must be called unconditionally (Rules of Hooks), so we call
  // them before the early return and handle the undefined visit case below.
  const milestones = useMilestones(visit?.projectId ?? "");
  const reports = useReports();

  if (!visit) {
    if (isFetchingVisits) {
      return <div className="flex items-center justify-center py-16 text-sm text-mute">Loading site visit…</div>;
    }
    return <EmptyState title="Site visit not found" body="It may have been removed. Return to the site visits list to start a new one." />;
  }
  // Scoped to this visit's own queued actions, not the whole app's queue -
  // a pending raise-ask from an unrelated reporter session showing up here
  // would just be confusing.
  const isSiteVisitAction = (a: { type: string; payload: unknown }) =>
    a.type.startsWith("siteVisit") && (a.payload as { visitId?: string })?.visitId === visit.id;
  const queueCount = pending.filter(isSiteVisitAction).length + queueSyncing.filter(isSiteVisitAction).length;
  const project = projects.find((p) => p.id === visit.projectId);
  const milestone = milestones.find((m) => m.id === visit.milestoneId);
  const report = reports.find((r) => r.id === visit.reportId);
  const isLive = visit.status === "in_progress";

  // Template questions oversight has added since this visit was started.
  const checklistLabels = new Set(visit.checklist.map((c) => c.label));
  const newQuestions = questions.filter((q) => !checklistLabels.has(q.label));

  return (
    <>
    <div className="space-y-5">
      <Link to="/stratus/portfolio/site-visits" className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">
        <ArrowLeft className="h-3 w-3" /> All site visits
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">{project?.name}</h2>
          <p className="mt-0.5 text-sm text-mute">
            {milestone ? `${milestone.name} milestone` : "Project visit"}
            {people[visit.visitorId]?.fullName ? ` · Visitor ${people[visit.visitorId].fullName}` : ""}
          </p>
        </div>
        <Pill variant={visit.status === "complete" ? "success" : visit.status === "in_progress" ? "info" : "neutral"}>
          {visit.status === "complete" ? "Complete" : visit.status === "in_progress" ? "In progress" : "Planned"}
        </Pill>
      </div>

      {(!isOnline || queueCount > 0) && (
        <div className="flex items-center gap-2 rounded-md border border-warn/30 bg-warnSoft px-3 py-2 text-sm text-warn">
          <CloudOff className="h-4 w-4 shrink-0" />
          {!isOnline
            ? queueCount > 0
              ? `Offline — ${queueCount} change${queueCount === 1 ? "" : "s"} from this visit saved, waiting to sync.`
              : "Offline — judgements and captures are being saved and will sync when you're back online."
            : `Syncing ${queueCount} change${queueCount === 1 ? "" : "s"} from this visit…`}
        </div>
      )}

      {visit.status === "planned" && (
        <div className="rounded-lg border border-line bg-card p-4">
          <p className="text-sm text-mute">This visit is planned. Begin it when you are on site to capture observations.</p>
          <Button className="mt-3" disabled={beginning} onClick={() => begin(visit.id)}>Begin visit</Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Checklist */}
        <div className="space-y-3 lg:col-span-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Checklist · the questions oversight has set</h3>
          {isLive && newQuestions.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand/30 bg-brandSoft/40 p-3">
              <p className="text-xs text-ink">
                Oversight added <span className="data font-semibold">{newQuestions.length}</span> new question{newQuestions.length > 1 ? "s" : ""} since this visit began. Pull them in without affecting anything you've already recorded.
              </p>
              <Button
                size="sm"
                variant="secondary"
                disabled={syncing}
                onClick={async () => {
                  setSyncing(true);
                  try {
                    await sync(visit.id);
                    toast.success("New questions added to this visit");
                  } catch {
                    toast.error("Could not pull in the new questions. Please try again.");
                  } finally {
                    setSyncing(false);
                  }
                }}
              >
                <RefreshCw className="h-3.5 w-3.5" /> Pull in new questions
              </Button>
            </div>
          )}
          {visit.checklist.map((item) => (
            <ChecklistRow
              key={item.id}
              item={item}
              captures={visit.captures.filter((c) => item.captureIds.includes(c.id))}
              live={isLive}
              onJudge={(j) => judge(item.id, j)}
              onCapture={(input) => capture({ ...input, checklistItemId: item.id })}
              onPreview={setPreviewUrl}
            />
          ))}
        </div>

        {/* Live analysis panel */}
        <div className="space-y-2">
          {/* TODO(heimdall): AI analyst not built yet — re-enable the live-analysis
              attribution ("{analyst}, the AI analyst" + capture-comparison note) when Heimdall ships.
          <div className="rounded-lg border border-line bg-card p-3">
            <HeimdallAttribution name={analystName} firstMention />
            <p className="mt-1 text-xs text-mute">Each capture is compared against the ledger's claims and recent evidence as you record it.</p>
          </div>
          */}
          {visit.checklist.filter((c) => c.judgement !== "unverified" || c.captureIds.length > 0).length === 0 ? (
            null
          ) : (
            visit.checklist
              .filter((c) => c.judgement !== "unverified" || c.captureIds.length > 0)
              .map((c) => (
                <Observation
                  key={c.id}
                  item={c}
                  captures={visit.captures.filter((cap) => c.captureIds.includes(cap.id))}
                  milestoneName={milestone?.name}
                  analystName={analystName}
                />
              ))
          )}
        </div>
      </div>

      {/* Wrap-up */}
      {isLive && (
        <div className="rounded-lg border border-line bg-card p-4">
          <h3 className="text-sm font-semibold text-ink">Wrap up</h3>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Summary: what you verified, any discrepancies, and recommended actions."
            className="mt-2 h-24 w-full rounded-md border border-line p-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <Button className="mt-2" disabled={completing} onClick={async () => { await complete(visit.id, summary.trim() || undefined); toast.success("Visit completed"); }}>
            Complete visit
          </Button>
        </div>
      )}

      {visit.status === "complete" && (
        <div className="rounded-lg border border-line bg-card p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink"><FileText className="h-4 w-4 text-brand" /> Visit report</h3>
          {visit.summary && <p className="mt-1.5 text-sm text-mute">{visit.summary}</p>}
          {/* TODO(heimdall): AI analyst not built yet — re-enable the "Draft with {analyst}"
              report generation and the AI-draft review/approve state when Heimdall ships.
              The "Filed in Reports" link (for an already-reviewed report) stays live. */}
          {!report ? (
            /* TODO(heimdall): re-enable draft generation:
            <>
              <p className="mt-2 text-xs text-mute">Generate a drafted report from the captures and findings. You review and approve it before it files.</p>
              <Button className="mt-2" disabled={generating} onClick={async () => { await generate(visit.id); toast.success(`${analystName} drafted the visit report for your review`); }}>
                Draft with {analystName}
              </Button>
            </>
            */
            null
          ) : report.reviewedById ? (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-sm text-ok"><ShieldCheck className="h-4 w-4" /> Filed in Reports</span>
              <Link to={`/stratus/reports/${report.id}`} className="text-sm font-medium text-brand hover:underline">Open report</Link>
            </div>
          ) : (
            /* TODO(heimdall): re-enable the AI-draft review/approve state:
            <div className="mt-2 space-y-2">
              <p className="text-xs text-mute">Draft ready, attributed to {analystName}. Review it, then approve to file it and attach any discrepancies as findings.</p>
              <div className="flex flex-wrap gap-2">
                <Link to={`/stratus/reports/${report.id}`} className="inline-flex items-center rounded-md border border-line px-3 py-1.5 text-sm font-medium text-ink hover:bg-subtle">Review draft</Link>
                <Button disabled={approvingVisit} onClick={async () => { await approveVisit(visit.id); toast.success("Visit report approved and filed"); }}>Approve and file</Button>
              </div>
            </div>
            */
            null
          )}
        </div>
      )}
    </div>

    {previewUrl && <CapturePreview url={previewUrl} onClose={() => setPreviewUrl(null)} />}
    </>
  );
}

// Full-size photo preview. Click the backdrop or the close button (or press Escape) to dismiss.
function CapturePreview({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo preview"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>
      <img
        src={url}
        alt="Site capture"
        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// Judgement chip colours match the Pill vocabulary (success/warn/danger/neutral),
// shown solid when selected and as a quiet outline when not.
const JUDGEMENT_CHIP_SELECTED: Record<VisitJudgement, string> = {
  as_claimed: "bg-okSoft text-okStrong",
  partial: "bg-warnSoft text-warnStrong",
  not_as_claimed: "bg-badSoft text-badStrong",
  unverified: "bg-neutralSoft text-mute",
};

type CapturePayload = { type: "photo" | "note"; note?: string; url?: string; file?: File };

function ChecklistRow({
  item,
  captures,
  live,
  onJudge,
  onCapture,
  onPreview,
}: {
  item: VisitChecklistItem;
  captures: VisitCapture[];
  live: boolean;
  onJudge: (j: VisitJudgement) => void;
  onCapture: (input: CapturePayload) => void;
  onPreview: (url: string) => void;
}) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  function saveNote() {
    const note = noteText.trim();
    if (!note) return;
    onCapture({ type: "note", note });
    setNoteText("");
    setNoteOpen(false);
  }

  function capturePhoto(file: File) {
    // The data URL is for instant local preview (demo mode, and the
    // optimistic cache patch shown before the real upload finishes) - the
    // real File is what actually gets uploaded for a real/queued capture
    // (see useChecklistActions.capture in useMutations.ts).
    const reader = new FileReader();
    reader.onload = () => {
      onCapture({ type: "photo", url: String(reader.result), note: file.name, file });
    };
    reader.readAsDataURL(file);
  }

  function onPhotoChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) capturePhoto(file);
  }

  // Single-file, matching onPhotoChosen's own e.target.files?.[0] semantics -
  // if several files are dropped at once, only the first becomes a capture.
  const fileDrop = useFileDrop({ onFiles: (files) => { if (files[0]) capturePhoto(files[0]); }, disabled: !live });

  return (
    <div
      onDragOver={fileDrop.onDragOver}
      onDragLeave={fileDrop.onDragLeave}
      onDrop={fileDrop.onDrop}
      className={cn(
        "rounded-lg border p-3 transition-colors",
        fileDrop.isDraggingOver ? "border-brand bg-brandSoft" : "border-line bg-card",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 break-words text-sm font-medium text-ink">{item.label}</p>
        {captures.length > 0 && (
          <span className="flex shrink-0 items-center gap-1 text-xs text-faint"><Camera className="h-3 w-3" /> <span className="data">{captures.length}</span></span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {live ? (
          // Editing on site: offer all four judgements.
          JUDGEMENT_ORDER.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => onJudge(key)}
              className={
                // min-h-11: these are the core field-recording controls -
                // the primary interaction of the whole site-visit flow, so
                // a real 44px touch target matters more here than most
                // chips elsewhere (often tapped one-handed/in the field).
                "min-h-11 rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                (item.judgement === key
                  ? JUDGEMENT_CHIP_SELECTED[key]
                  : "border border-line text-mute hover:bg-subtle")
              }
            >
              {JUDGEMENT_META[key].label}
            </button>
          ))
        ) : (
          // Viewing the record: show only the answer that was given.
          <span className={"rounded-full px-2.5 py-1 text-xs font-medium " + JUDGEMENT_CHIP_SELECTED[item.judgement]}>
            {JUDGEMENT_META[item.judgement].label}
          </span>
        )}
      </div>
      {captures.length > 0 && <CaptureMeta captures={captures} onPreview={onPreview} />}
      {live && (
        <div className="mt-2 space-y-2">
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-line px-3 py-1 text-xs text-ink hover:bg-subtle">
              <Camera className="h-3.5 w-3.5" /> Add photo
            </button>
            <button type="button" onClick={() => setNoteOpen((o) => !o)} className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-line px-3 py-1 text-xs text-mute hover:bg-subtle">
              <StickyNote className="h-3.5 w-3.5" /> Add note
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoChosen} />
          </div>
          {noteOpen && (
            <div className="space-y-2">
              <Textarea
                autoFocus
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveNote();
                  if (e.key === "Escape") { setNoteOpen(false); setNoteText(""); }
                }}
                placeholder="What did you observe on site for this question?"
                className="h-20"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveNote} disabled={!noteText.trim()}>
                  <Check className="h-3.5 w-3.5" /> Save note
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setNoteOpen(false); setNoteText(""); }}>
                  <X className="h-3.5 w-3.5" /> Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Captures rendered with their content — note text and photo thumbnail — plus
// meta in the "data" voice: geo-tag flag and capture age.
function CaptureMeta({ captures, onPreview }: { captures: VisitCapture[]; onPreview: (url: string) => void }) {
  return (
    <ul className="mt-2 space-y-2 border-t border-hairline pt-2">
      {captures.map((c) => (
        <li key={c.id} className="flex items-start gap-2 text-xs text-mute">
          {c.type === "photo" && c.url ? (
            <button
              type="button"
              onClick={() => onPreview(c.url!)}
              className="flex-none rounded-md ring-offset-1 focus:outline-none focus:ring-2 focus:ring-brand"
              aria-label="Preview photo"
              title="Click to preview"
            >
              <img src={c.url} alt="Site capture" className="h-12 w-12 rounded-md border border-line object-cover transition-opacity hover:opacity-80" />
            </button>
          ) : c.type === "photo" ? (
            <Camera className="mt-0.5 h-3 w-3 flex-none text-faint" />
          ) : (
            <StickyNote className="mt-0.5 h-3 w-3 flex-none text-faint" />
          )}
          <div className="min-w-0 flex-1">
            {c.note && <p className="break-words text-ink">{c.note}</p>}
            <div className="mt-0.5 flex items-center gap-2 text-faint">
              <span>{c.type === "photo" ? "Photo" : "Note"}</span>
              {c.geo && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> geo-tagged</span>}
              <span className="data ml-auto">{formatRelativeShort(c.takenAt)} ago</span>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Observation({
  item,
  captures,
  milestoneName,
  analystName,
}: {
  item: VisitChecklistItem;
  captures: VisitCapture[];
  milestoneName?: string;
  analystName: string;
}) {
  const severity = item.judgement === "not_as_claimed" ? "serious" : item.judgement === "partial" ? "attention" : "info";
  const text =
    item.judgement === "as_claimed"
      ? `Captures for '${item.label}' are consistent with what the ledger claims${milestoneName ? ` for ${milestoneName}` : ""}.`
      : item.judgement === "not_as_claimed"
      ? `'${item.label}' is not as claimed on site. Recommend querying the related spend with the captured evidence attached.`
      : item.judgement === "partial"
      ? `'${item.label}' only partly matches the ledger claim, worth a closer look.`
      : `Captured '${item.label}'; awaiting your judgement.`;
  const latest = captures.reduce<VisitCapture | undefined>(
    (a, c) => (!a || new Date(c.takenAt) > new Date(a.takenAt) ? c : a),
    undefined,
  );
  return (
    <div className="rounded-lg border border-line bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        {item.judgement === "as_claimed" ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-okStrong">Pass</span>
        ) : (
          <SeverityChip severity={severity} />
        )}
        {captures.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-faint">
            <MapPin className="h-3 w-3" /> geo-tagged · <span className="data">{captures.length}</span>
          </span>
        )}
        {latest && <span className="data ml-auto text-[11px] text-faint">{formatRelativeShort(latest.takenAt)} ago</span>}
      </div>
      <p className="mt-1.5 text-sm text-mute">{text}</p>
      {/* TODO(heimdall): re-enable the per-observation analyst attribution when Heimdall ships.
      <div className="mt-2 border-t border-hairline pt-2">
        <HeimdallAttribution name={analystName} />
      </div>
      */}
    </div>
  );
}
